/**
 * Board Meeting Report Generator
 * One-click PDF generation for board meetings
 */
import { useState, useEffect, useRef } from 'react';
import { 
  FileText, Download, Printer, TrendingUp, TrendingDown, DollarSign, 
  Users, Target, AlertTriangle, CheckCircle, Clock, Calendar,
  Building2, PieChart, ArrowRight, Briefcase, RefreshCw, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BoardReportPage = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [printing, setPrinting] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/reports/board-meeting`);
      if (!res.ok) throw new Error('Failed to fetch report');
      const data = await res.json();
      setReport(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    setPrinting(true);
    setTimeout(() => {
      window.print();
      setPrinting(false);
    }, 500);
  };

  const formatCurrency = (value) => {
    return `RM ${Number(value || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercent = (value) => {
    const num = Number(value || 0);
    const sign = num >= 0 ? '+' : '';
    return `${sign}${num.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="board-report-loading">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-red-600 mx-auto mb-3" />
          <p className="text-gray-600">Menjana laporan...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center" data-testid="board-report-error">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <h3 className="text-lg font-bold text-red-800">Ralat</h3>
        <p className="text-red-600 mt-1">{error}</p>
        <button 
          onClick={fetchReport}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Cuba Semula
        </button>
      </div>
    );
  }

  if (!report) return null;

  const { executive_summary, financial_summary, operational_metrics, goals_progress, channel_performance, alerts, recommendations } = report;
  const healthScore = Math.round(executive_summary?.health_score || 0);
  const healthColor = healthScore >= 70 ? 'text-emerald-600' : healthScore >= 50 ? 'text-amber-600' : 'text-red-600';
  const healthBg = healthScore >= 70 ? 'bg-emerald-50' : healthScore >= 50 ? 'bg-amber-50' : 'bg-red-50';

  return (
    <div className="space-y-6" data-testid="board-report-page">
      {/* Header with Actions */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-6 text-white print:bg-gray-900">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/10 rounded-xl">
              <FileText className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Board Meeting Report</h1>
              <p className="text-gray-300 text-sm mt-1">
                {report.company_name} • Period: {report.report_period}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 print:hidden">
            <button
              onClick={fetchReport}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={handlePrint}
              disabled={printing}
              className="flex items-center gap-2 px-4 py-2 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-colors font-medium"
              data-testid="print-report-btn"
            >
              {printing ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Printer className="w-4 h-4" />
              )}
              Print / Save PDF
            </button>
          </div>
        </div>
        
        {/* Generated Time */}
        <div className="mt-4 flex items-center gap-2 text-gray-400 text-sm">
          <Clock className="w-4 h-4" />
          <span>Dijana pada: {new Date(report.generated_at).toLocaleString('ms-MY')}</span>
        </div>
      </div>

      {/* Print Content */}
      <div ref={printRef} className="print:p-8">
        {/* Executive Summary */}
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm mb-6">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
            <Briefcase className="w-5 h-5 text-gray-600" />
            Executive Summary
          </h2>
          
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            {/* Health Score */}
            <div className={`${healthBg} rounded-xl p-4 text-center`}>
              <p className="text-sm text-gray-600 mb-1">Business Health</p>
              <p className={`text-4xl font-bold ${healthColor}`}>{healthScore}</p>
              <p className="text-xs text-gray-500 mt-1">/ 100</p>
            </div>
            
            {/* Key Stats */}
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-sm text-gray-600 mb-1">Critical Issues</p>
              <p className={`text-3xl font-bold ${executive_summary.critical_issues > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                {executive_summary.critical_issues}
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-sm text-gray-600 mb-1">Warnings</p>
              <p className={`text-3xl font-bold ${executive_summary.warnings > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                {executive_summary.warnings}
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-sm text-gray-600 mb-1">Total Staff</p>
              <p className="text-3xl font-bold text-blue-600">{operational_metrics.staff_count}</p>
            </div>
          </div>
          
          {/* Key Highlights */}
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-sm font-semibold text-blue-800 mb-2">Key Highlights</p>
            <ul className="space-y-2">
              {executive_summary.key_highlights?.map((highlight, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-blue-700">
                  <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  {highlight}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Financial Summary */}
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm mb-6 print:break-before-page">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
            <DollarSign className="w-5 h-5 text-emerald-600" />
            Financial Summary
          </h2>
          
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            {/* Revenue */}
            <div className="bg-emerald-50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-emerald-700">Revenue</p>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  financial_summary.revenue.change_percent >= 0 
                    ? 'bg-emerald-100 text-emerald-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {formatPercent(financial_summary.revenue.change_percent)}
                </span>
              </div>
              <p className="text-2xl font-bold text-emerald-800">
                {formatCurrency(financial_summary.revenue.current)}
              </p>
              <p className="text-xs text-emerald-600 mt-1">
                Previous: {formatCurrency(financial_summary.revenue.previous)}
              </p>
            </div>
            
            {/* Expenses */}
            <div className="bg-red-50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-red-700">Expenses</p>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  financial_summary.expenses.change_percent <= 0 
                    ? 'bg-emerald-100 text-emerald-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {formatPercent(financial_summary.expenses.change_percent)}
                </span>
              </div>
              <p className="text-2xl font-bold text-red-800">
                {formatCurrency(financial_summary.expenses.current)}
              </p>
              <p className="text-xs text-red-600 mt-1">
                Previous: {formatCurrency(financial_summary.expenses.previous)}
              </p>
            </div>
            
            {/* Profit */}
            <div className={`${financial_summary.profit.current >= 0 ? 'bg-blue-50' : 'bg-orange-50'} rounded-xl p-4`}>
              <div className="flex items-center justify-between mb-2">
                <p className={`text-sm ${financial_summary.profit.current >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>Net Profit</p>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  financial_summary.profit.change_percent >= 0 
                    ? 'bg-emerald-100 text-emerald-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {formatPercent(financial_summary.profit.change_percent)}
                </span>
              </div>
              <p className={`text-2xl font-bold ${financial_summary.profit.current >= 0 ? 'text-blue-800' : 'text-orange-800'}`}>
                {formatCurrency(financial_summary.profit.current)}
              </p>
              <p className={`text-xs mt-1 ${financial_summary.profit.current >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                Margin: {financial_summary.profit.margin_current}%
              </p>
            </div>
            
            {/* Cash Position */}
            <div className={`rounded-xl p-4 ${
              financial_summary.cash_position.status === 'healthy' ? 'bg-emerald-50' :
              financial_summary.cash_position.status === 'warning' ? 'bg-amber-50' : 'bg-red-50'
            }`}>
              <p className={`text-sm ${
                financial_summary.cash_position.status === 'healthy' ? 'text-emerald-700' :
                financial_summary.cash_position.status === 'warning' ? 'text-amber-700' : 'text-red-700'
              }`}>Cash Position</p>
              <p className={`text-2xl font-bold mt-2 ${
                financial_summary.cash_position.status === 'healthy' ? 'text-emerald-800' :
                financial_summary.cash_position.status === 'warning' ? 'text-amber-800' : 'text-red-800'
              }`}>
                {formatCurrency(financial_summary.cash_position.balance)}
              </p>
              <p className={`text-xs mt-1 ${
                financial_summary.cash_position.status === 'healthy' ? 'text-emerald-600' :
                financial_summary.cash_position.status === 'warning' ? 'text-amber-600' : 'text-red-600'
              }`}>
                Runway: {financial_summary.cash_position.runway_months} bulan
              </p>
            </div>
          </div>
          
          {/* Revenue Breakdown */}
          {Object.keys(financial_summary.revenue.breakdown || {}).length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-semibold text-gray-700 mb-2">Revenue by Source</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {Object.entries(financial_summary.revenue.breakdown).map(([source, amount]) => (
                  <div key={source} className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 truncate">{source}</p>
                    <p className="text-sm font-semibold text-gray-800">{formatCurrency(amount)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Expense Breakdown */}
          {Object.keys(financial_summary.expenses.breakdown || {}).length > 0 && (
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Expenses by Category</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {Object.entries(financial_summary.expenses.breakdown)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 8)
                  .map(([category, amount]) => (
                  <div key={category} className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 truncate">{category}</p>
                    <p className="text-sm font-semibold text-gray-800">{formatCurrency(amount)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Goals Progress & Channel Performance */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Goals */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
              <Target className="w-5 h-5 text-purple-600" />
              Goals Progress
            </h2>
            
            {goals_progress && goals_progress.length > 0 ? (
              <div className="space-y-4">
                {goals_progress.map((goal, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-gray-800">{goal.name}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        goal.status === 'on_track' ? 'bg-emerald-100 text-emerald-700' :
                        goal.status === 'at_risk' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {goal.status === 'on_track' ? 'On Track' : goal.status === 'at_risk' ? 'At Risk' : 'Behind'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
                      <div 
                        className={`h-2 rounded-full ${
                          goal.status === 'on_track' ? 'bg-emerald-500' :
                          goal.status === 'at_risk' ? 'bg-amber-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(100, goal.progress)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>{goal.progress}%</span>
                      <span>{formatCurrency(goal.current)} / {formatCurrency(goal.target)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm text-center py-4">Tiada goals ditetapkan</p>
            )}
          </div>
          
          {/* Channels */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
              <PieChart className="w-5 h-5 text-blue-600" />
              Top Channel Performance
            </h2>
            
            {channel_performance && channel_performance.length > 0 ? (
              <div className="space-y-3">
                {channel_performance.map((channel, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-800">{channel.name}</p>
                      <p className="text-xs text-gray-500">
                        Revenue: {formatCurrency(channel.revenue)} | Cost: {formatCurrency(channel.cost)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${channel.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(channel.profit)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {channel.margin}% margin
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm text-center py-4">Tiada data channel</p>
            )}
          </div>
        </div>

        {/* Alerts & Recommendations */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Alerts */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Alerts & Issues
            </h2>
            
            {alerts && alerts.length > 0 ? (
              <div className="space-y-3">
                {alerts.map((alert, i) => (
                  <div key={i} className={`p-3 rounded-lg border ${
                    alert.type === 'critical' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
                  }`}>
                    <p className={`font-medium text-sm ${
                      alert.type === 'critical' ? 'text-red-800' : 'text-amber-800'
                    }`}>
                      {alert.message}
                    </p>
                    <p className={`text-xs mt-1 ${
                      alert.type === 'critical' ? 'text-red-600' : 'text-amber-600'
                    }`}>
                      Action: {alert.action}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
                <p className="text-emerald-700 font-medium">Tiada isu kritikal</p>
                <p className="text-gray-500 text-sm">Semua metrics dalam keadaan baik</p>
              </div>
            )}
          </div>
          
          {/* Recommendations */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
              <Target className="w-5 h-5 text-purple-600" />
              Recommendations
            </h2>
            
            <div className="space-y-3">
              {recommendations?.map((rec, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                  <div className="w-6 h-6 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {i + 1}
                  </div>
                  <p className="text-sm text-purple-800">{rec}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Operational Metrics */}
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm mt-6">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
            <Building2 className="w-5 h-5 text-gray-600" />
            Operational Metrics
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <Users className="w-6 h-6 text-blue-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-800">{operational_metrics.staff_count}</p>
              <p className="text-xs text-gray-500">Total Staff</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <DollarSign className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
              <p className="text-lg font-bold text-gray-800">{formatCurrency(operational_metrics.monthly_payroll)}</p>
              <p className="text-xs text-gray-500">Monthly Payroll</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <TrendingUp className="w-6 h-6 text-purple-600 mx-auto mb-2" />
              <p className="text-lg font-bold text-gray-800">{formatCurrency(operational_metrics.revenue_per_employee)}</p>
              <p className="text-xs text-gray-500">Revenue per Employee</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <Building2 className="w-6 h-6 text-amber-600 mx-auto mb-2" />
              <p className="text-lg font-bold text-gray-800">{formatCurrency(operational_metrics.total_assets)}</p>
              <p className="text-xs text-gray-500">Total Assets</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-gray-200 text-center text-xs text-gray-500">
          <p>{report.company_name} • Board Meeting Report • {report.report_period}</p>
          <p className="mt-1">Generated by UA Syarikat — Sistem Pengurusan Kewangan & HR</p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
          .print\\:break-before-page { break-before: page; }
          .print\\:bg-gray-900 { background-color: #111827 !important; }
          .print\\:p-8 { padding: 2rem; }
        }
      `}</style>
    </div>
  );
};

export default BoardReportPage;
